/**
 * PaymentMethodsPage — Manage saved payment methods.
 *
 * Layout matches Figma node 11370:1571:
 *   - "CARTÕES SALVOS" section: each card as a standalone row.
 *     Default card gets red border + red/10 tinted background.
 *     Card brand rendered as a styled pill (VISA dark-blue gradient, etc.)
 *     Kebab (···) opens an action sheet to set default or remove.
 *   - "ADICIONAR NOVO CARTÃO" section: individual Stripe elements
 *     (CardNumberElement, CardExpiryElement, CardCvcElement) + name input.
 *   - Lock-icon security note at the bottom.
 */

import { useState } from 'react';
import { Lock, Loader2, MoreHorizontal, Check } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  useSavedCards,
  useRemoveSavedCard,
  useSetDefaultSavedCard,
} from '../hooks/useSavedCard';
import { paymentMethodApi } from '../services/paymentMethodApi';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// ── Stripe element shared style ────────────────────────────────────────────────

const STRIPE_STYLE = {
  style: {
    base: {
      color: '#ffffff',
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif',
      '::placeholder': { color: 'rgba(255,255,255,0.35)' },
    },
    invalid: { color: '#f87171' },
  },
};

// ── Card brand pill ────────────────────────────────────────────────────────────

const BRAND_STYLES: Record<string, string> = {
  visa:       'bg-gradient-to-br from-[#1a1f71] to-[#0f1535]',
  mastercard: 'bg-gradient-to-br from-[#eb001b] to-[#f79e1b]',
  amex:       'bg-gradient-to-br from-[#007bc1] to-[#00457c]',
  discover:   'bg-gradient-to-br from-[#e65c00] to-[#f9d423]',
  default:    'bg-white/10',
};

function BrandPill({ brand }: { brand: string }) {
  const style = BRAND_STYLES[brand.toLowerCase()] ?? BRAND_STYLES.default;
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-[5px] w-[38px] h-[26px] shrink-0',
        style,
      )}
    >
      <span className="text-[9px] font-extrabold text-white tracking-[0.5px] uppercase">
        {brand.slice(0, 4).toUpperCase()}
      </span>
    </div>
  );
}

// ── Saved card row ─────────────────────────────────────────────────────────────

type SavedCard = {
  id: string;
  brand: string;
  last4: string;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
};

function CardRow({
  card,
  onSetDefault,
  onRemove,
  busy,
}: {
  card: SavedCard;
  onSetDefault: () => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const expiry = `${String(card.exp_month ?? '').padStart(2, '0')}/${card.exp_year}`;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const isExpired =
    card.exp_year != null &&
    card.exp_month != null &&
    (card.exp_year < currentYear ||
      (card.exp_year === currentYear && card.exp_month < currentMonth));
  const expiryLastDay =
    card.exp_year != null && card.exp_month != null
      ? new Date(card.exp_year, card.exp_month, 0)
      : null;
  const daysToExpiry =
    expiryLastDay != null
      ? Math.floor((expiryLastDay.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      : null;
  const isExpiringSoon = !isExpired && daysToExpiry != null && daysToExpiry <= 30;

  return (
    <div
      data-testid="pm-card-row"
      data-card-id={card.id}
      data-is-default={card.is_default ? 'true' : 'false'}
      data-is-expired={isExpired ? 'true' : 'false'}
      className={cn(
        'relative rounded-xl border px-[14px] py-[18px] flex items-center gap-3 transition-colors',
        isExpired
          ? 'border-amber-500/60 bg-amber-500/10'
          : card.is_default
            ? 'border-[#d72e16] bg-[rgba(215,46,22,0.10)]'
            : 'border-white/10 bg-white/[0.03]',
      )}
    >
      <BrandPill brand={card.brand} />

      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-white font-mono tracking-[0.5px]">
          •••• {card.last4}
        </p>
        {isExpired ? (
          <p className="text-[11px] text-amber-400 mt-0.5 font-medium">
            {t('premium.manage.pm_expired_label')}
          </p>
        ) : (
          <p className={cn('text-[11px] mt-0.5', isExpiringSoon ? 'text-amber-400/80' : 'text-white/50')}>
            {t('premium.manage.pm_expires', { date: expiry })}
            {isExpiringSoon && (
              <span className="ml-1.5">· {t('premium.manage.pm_expiring_soon')}</span>
            )}
            {card.is_default && !isExpiringSoon && (
              <span className="ml-1.5 text-white/40">· {t('premium.manage.pm_default')}</span>
            )}
          </p>
        )}
      </div>

      {/* Kebab / more button */}
      <button
        type="button"
        data-testid="pm-card-menu"
        onClick={() => setMenuOpen((o) => !o)}
        disabled={busy}
        className="w-8 h-8 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Card options"
      >
        {busy ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <MoreHorizontal size={18} />
        )}
      </button>

      {/* Dropdown menu */}
      {menuOpen && (
        <div className="absolute right-3 top-12 z-20 min-w-[160px] rounded-xl border border-white/10 bg-[#231515] shadow-xl overflow-hidden">
          {!card.is_default && (
            <button
              type="button"
              data-testid="pm-card-set-default"
              onClick={() => { setMenuOpen(false); onSetDefault(); }}
              className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/10 flex items-center gap-2"
            >
              <Check size={14} className="text-white/50" />
              {t('premium.manage.pm_set_default')}
            </button>
          )}
          <button
            type="button"
            data-testid="pm-card-remove"
            onClick={() => { setMenuOpen(false); onRemove(); }}
            className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
          >
            {t('premium.manage.pm_remove')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Section label ──────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-bold tracking-[2.5px] text-white/50 uppercase px-1">
      {children}
    </p>
  );
}

// ── Stripe field wrapper ───────────────────────────────────────────────────────

function StripeField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-semibold tracking-[1px] uppercase text-white/50">{label}</p>
      <div className="h-[45px] rounded-xl border border-white/10 bg-[#2c2222] px-[14px] flex items-center">
        {children}
      </div>
    </div>
  );
}

// ── Add card form ──────────────────────────────────────────────────────────────

function AddCardForm({ hasSavedCard = false }: { hasSavedCard?: boolean }) {
  const { t } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const { refetch } = useSavedCards();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNumberComplete, setIsNumberComplete] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements) return;
    setIsSaving(true);
    setError(null);

    try {
      const setup = await paymentMethodApi.createSetupIntent();
      const cardNumberEl = elements.getElement(CardNumberElement);
      if (!cardNumberEl) throw new Error('Card input not ready');

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(
        setup.client_secret,
        {
          payment_method: {
            card: cardNumberEl,
            billing_details: name ? { name } : undefined,
          },
        },
      );

      if (setupError) throw new Error(setupError.message || 'Failed to save card');
      if (!setupIntent?.id) throw new Error('Setup intent did not complete');

      await paymentMethodApi.finalizeSetupIntent(setupIntent.id);
      await refetch();
      cardNumberEl.clear();
      elements.getElement(CardExpiryElement)?.clear();
      elements.getElement(CardCvcElement)?.clear();
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save card');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionLabel>
        {hasSavedCard ? t('premium.manage.pm_change_section') : t('premium.manage.pm_add_section')}
      </SectionLabel>

      <div className="flex flex-col gap-3">
        {/* Card number */}
        <StripeField label={t('premium.manage.pm_field_number')}>
          <CardNumberElement
            options={STRIPE_STYLE}
            className="w-full"
            onChange={(e) => setIsNumberComplete(e.complete)}
          />
        </StripeField>

        {/* Expiry + CVC side by side */}
        <div className="grid grid-cols-2 gap-3">
          <StripeField label={t('premium.manage.pm_field_expiry')}>
            <CardExpiryElement options={STRIPE_STYLE} className="w-full" />
          </StripeField>
          <StripeField label={t('premium.manage.pm_field_cvc')}>
            <CardCvcElement options={STRIPE_STYLE} className="w-full" />
          </StripeField>
        </div>

        {/* Cardholder name */}
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold tracking-[1px] uppercase text-white/50">
            {t('premium.manage.pm_field_name')}
          </p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('premium.manage.pm_field_name_placeholder')}
            className="h-[45px] rounded-xl border border-white/10 bg-[#2c2222] px-[14px] text-[14px] text-white placeholder:text-white/35 outline-none focus:border-white/25 transition-colors"
          />
        </div>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}

      <Button
        variant="primary"
        size="lg"
        onClick={handleSave}
        disabled={isSaving || !stripe || !elements || !isNumberComplete}
        className="w-full rounded-full"
      >
        {isSaving ? (
          <span className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            {t('premium.manage.pm_saving')}
          </span>
        ) : (
          t('premium.manage.pm_add_cta')
        )}
      </Button>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PaymentMethodsPage() {
  const { t } = useI18n();
  const { cards, isLoading, refetch } = useSavedCards();
  const removeMutation = useRemoveSavedCard();
  const setDefaultMutation = useSetDefaultSavedCard();
  const [pendingCardId, setPendingCardId] = useState<string | null>(null);

  const sortedCards = [...cards].sort((a, b) => Number(b.is_default) - Number(a.is_default));

  return (
    <SubPageLayout title={t('premium.manage.payment_methods')} hideBottomNav>
      <div className="max-w-md mx-auto pt-4 pb-12 flex flex-col gap-6 px-0">

        {isLoading ? (
          <div className="flex items-center gap-2 px-1 pt-4">
            <Loader2 size={16} className="animate-spin text-white/50" />
            <span className="text-sm text-white/50">{t('premium.manage.pm_loading')}</span>
          </div>
        ) : (
          <>
            {/* ── Saved cards ──────────────────────────────────────────── */}
            <div className="flex flex-col gap-3">
              <SectionLabel>{t('premium.manage.pm_saved_cards')}</SectionLabel>

              {sortedCards.length === 0 ? (
                <p data-testid="pm-no-cards" className="text-sm text-white/40 px-1">{t('premium.manage.pm_no_cards')}</p>
              ) : (
                sortedCards.map((card) => (
                  <CardRow
                    key={card.id}
                    card={card}
                    busy={(removeMutation.isPending || setDefaultMutation.isPending) && pendingCardId === card.id}
                    onSetDefault={async () => {
                      setPendingCardId(card.id);
                      try {
                        await setDefaultMutation.mutateAsync(card.id);
                        await refetch();
                      } finally {
                        setPendingCardId(null);
                      }
                    }}
                    onRemove={async () => {
                      setPendingCardId(card.id);
                      try {
                        await removeMutation.mutateAsync(card.id);
                        await refetch();
                      } finally {
                        setPendingCardId(null);
                      }
                    }}
                  />
                ))
              )}
            </div>

            {/* ── Add new card ─────────────────────────────────────────── */}
            <Elements stripe={stripePromise}>
              <AddCardForm hasSavedCard={sortedCards.length > 0} />
            </Elements>
          </>
        )}

        {/* ── Security note ─────────────────────────────────────────────── */}
        <div className="flex items-start gap-3 px-1">
          <Lock size={14} className="mt-0.5 shrink-0 text-white/30" />
          <p className="text-[11px] text-white/40 leading-relaxed">
            {t('premium.manage.pm_security_note')}
          </p>
        </div>
      </div>
    </SubPageLayout>
  );
}
